import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { PageDocument } from '@/lib/db/rxdb/hooks/entities/pages'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import { useBacklinks, useEntityLinks, useProject } from '@/lib/db/rxdb/hooks'
import { ENTITY_TYPES } from '@/lib/constants/entityTypes'
import { LocationSwatch } from '@/components/app/sidebars/spaces/SpaceSwatch'

/** How many distinct tasks link to or from this doc. */
function useLinkedTaskCount(pageId: string): number {
  const { links } = useEntityLinks(pageId)
  const { backlinks } = useBacklinks(pageId)
  return useMemo(() => {
    const ids = new Set<string>()
    for (const l of links) if (l.targetEntityType === ENTITY_TYPES.WORK_ITEM) ids.add(l.targetEntityId)
    for (const l of backlinks) if (l.sourceEntityType === ENTITY_TYPES.WORK_ITEM) ids.add(l.sourceEntityId)
    return ids.size
  }, [links, backlinks])
}

/**
 * Where a doc lives (design 22b): "Space doc · everyone in Zyro" or
 * "Zyro Platform doc · linked to 2 tasks".
 */
export function DocLevelChip({ page, location }: { page: PageDocument; location: ProjectDocument }) {
  const { t } = useTranslation('projects')
  const { project: parentSpace } = useProject(location.parentProjectId ?? undefined)
  const linkedTasks = useLinkedTaskCount(page.id)
  const isSpaceDoc = !location.parentProjectId

  const detail = isSpaceDoc
    ? t('docs.level.everyoneIn', { name: location.name })
    : linkedTasks > 0
      ? t('docs.level.linkedTasks', { count: linkedTasks })
      : parentSpace
        ? t('docs.level.inSpace', { name: parentSpace.name })
        : null

  return (
    <p className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-[12px] text-muted-foreground" data-testid="doc-level-chip">
      <span className="inline-flex max-w-full items-center gap-1.5 rounded-md bg-secondary px-2 py-[3px] font-medium text-foreground">
        <LocationSwatch location={location} size={12} />
        <span className="truncate">
          {isSpaceDoc ? t('docs.level.spaceDoc') : t('docs.level.projectDoc', { name: location.name })}
        </span>
      </span>
      {detail && <span className="truncate">· {detail}</span>}
    </p>
  )
}
